import { isSyntheticRenewalOfferId } from '@ourbranch/offer-utils';

/**
 * Throwaway context prototype; no store reads, requests, or production wiring.
 * loaded is an adapter snapshot: {status, view, offer, policy, account, version, search, previewId}.
 * Ready/current must be explicit; the adapter must include query and in-memory preview state.
 */
export const getPageContext = (pathname, loaded = {}) => {
  const context = {
    kind: 'none',
    status: 'unsupported',
    offerId: null,
    policyId: null,
    accountId: null,
    view: 'unknown',
    version: null
  };
  if (typeof pathname !== 'string' || /[?#]/.test(pathname)) {
    return context;
  }

  const path = pathname.replace(/\/$/, '');
  const offer = path.match(/^\/offer\/([^/]+)$/);
  const policy = path.match(/^\/customer\/([^/]+)\/policy\/([^/]+)(?:\/(.+))?$/);
  const account = path.match(/^\/customer\/([^/]+)$/);
  if (/^\/branch-plus\/[^/]+$/.test(path) || /^\/customer\/[^/]+\/branch-plus\/[^/]+$/.test(path)) {
    return { ...context, kind: 'branch-plus', view: 'branch-plus' };
  }
  if (path === '/quote') {
    return { ...context, kind: 'offer', view: 'unsaved' };
  }
  if (offer) {
    context.kind = 'offer';
    if (isSyntheticRenewalOfferId(offer[1])) {
      return { ...context, view: 'synthetic' };
    }
  } else if (policy) {
    context.kind = 'policy';
    if (policy[3]) {
      const historical = policy[3].match(/^version-compare\/([^/]+)$/);
      return { ...context, view: historical ? 'historical' : 'preview', version: historical?.[1] ?? null };
    }
  } else if (account) {
    context.kind = 'account';
  } else {
    return context;
  }

  if (loaded.previewId || new URLSearchParams(loaded.search ?? '').has('previewId')) {
    return { ...context, view: 'preview' };
  }
  context.view = loaded.view ?? 'unknown';
  if (loaded.status !== 'ready') {
    return { ...context, status: loaded.status === 'error' ? 'error' : 'loading' };
  }
  if (context.view !== 'current') {
    return context;
  }

  // A singleton store can still hold A while route B is rendering or loading.
  let matches;
  if (offer) {
    matches = loaded.offer?.id === offer[1];
  } else if (policy) {
    matches = loaded.account?.id === policy[1] && loaded.policy?.id === policy[2];
  } else {
    matches = loaded.account?.id === account[1];
  }
  if (!matches) {
    return { ...context, status: 'mismatch' };
  }

  return {
    ...context,
    status: 'ready',
    offerId: offer ? loaded.offer.id : null,
    policyId: policy ? loaded.policy.id : null,
    accountId: policy || account ? loaded.account.id : null,
    version: policy ? (loaded.version ?? null) : null
  };
};

/** Returns only identity fields; tool-specific decisions remain blank for the operator. */
export const prefillForTool = (toolId, context) => {
  if (context?.status !== 'ready' || context.view !== 'current') {
    return {};
  }
  switch (toolId) {
    case 'offer-overrides':
      return context.kind === 'offer' && context.offerId ? { offerId: context.offerId } : {};
    case 'update-policy-rep':
    case 'policy-overrides':
      return context.kind === 'policy' && context.policyId ? { policyId: context.policyId } : {};
    case 'set-affinity-code-for-member':
      return ['account', 'policy'].includes(context.kind) && context.accountId ? { account_id: context.accountId } : {};
    default:
      return {};
  }
};
